import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import { useState } from 'react';
import TicketedEventTagsList from '@Components/TicketedEventTags/List';
import TicketedEventTagForm from '@Components/TicketedEventTags/Form';
import Breadcrumbs from '@Components/Breadcrumbs';
import Heading from '@Components/Heading';
import type { TicketedEventTag } from '@Types/ticketedEvents';

const CREATE_FORM_ID = 'create-tag-form';
const EDIT_FORM_ID = 'edit-tag-form';

export default function EventTags() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editTag, setEditTag] = useState<TicketedEventTag | null>(null);

  const handleRowClick = (row: { original: TicketedEventTag }) => {
    setEditTag(row.original);
    setEditOpen(true);
  };

  return (
    <div>
      <Breadcrumbs
        navList={[
          { name: 'Events', href: '/events' },
          { name: 'Tags', href: '/events/tags' },
        ]}
      />
      <div className="mt-8 mb-4 flex items-center justify-between">
        <div>
          <Heading>Tags</Heading>
        </div>
        <Sheet open={createOpen} onOpenChange={setCreateOpen}>
          <SheetTrigger asChild>
            <Button variant="primary">Create new tag</Button>
          </SheetTrigger>

          <SheetContent side="right" className="w-full sm:max-w-xl">
            <SheetHeader>
              <SheetTitle>Create tag</SheetTitle>
              <SheetDescription>
                Add a new tag with a name and description.
              </SheetDescription>
            </SheetHeader>

            <SheetBody className="grow">
              <TicketedEventTagForm
                onSuccess={() => setCreateOpen(false)}
                submitLabel="Create"
                formId={CREATE_FORM_ID}
                hideInternalActions
              />
            </SheetBody>

            <SheetFooter className="mt-4">
              <Button
                type="submit"
                form={CREATE_FORM_ID}
                variant="primary"
                className="grow"
              >
                Create
              </Button>
              <SheetClose asChild>
                <Button variant="secondary" type="button" className="grow">
                  Cancel
                </Button>
              </SheetClose>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </div>

      <TicketedEventTagsList onRowClick={handleRowClick} />

      {/* Edit Sheet */}
      <Sheet
        open={editOpen}
        onOpenChange={(isOpen) => {
          setEditOpen(isOpen);
          if (!isOpen) setEditTag(null);
        }}
      >
        <SheetContent side="right" className="w-full sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>Edit tag</SheetTitle>
            <SheetDescription>
              Update the tag name and description.
            </SheetDescription>
          </SheetHeader>

          <SheetBody className="grow">
            {editTag && (
              <TicketedEventTagForm
                key={editTag.id}
                editId={editTag.id}
                defaultValues={{
                  name: editTag.name,
                  description: editTag.description ?? '',
                }}
                onSuccess={() => {
                  setEditOpen(false);
                  setEditTag(null);
                }}
                submitLabel="Update"
                formId={EDIT_FORM_ID}
                hideInternalActions
              />
            )}
          </SheetBody>

          <SheetFooter className="mt-4">
            <Button
              type="submit"
              form={EDIT_FORM_ID}
              variant="primary"
              className="grow"
            >
              Update
            </Button>
            <SheetClose asChild>
              <Button variant="secondary" type="button" className="grow">
                Cancel
              </Button>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
